/**
 * SettingsPanel Component
 * 
 * Comprehensive settings panel for Live Location Activity Dashboard.
 * Allows users to customize all aspects of dashboard behavior including
 * carousel, cache, heatmap, and display settings.
 * 
 * Features:
 * - Carousel Settings: dwell time, queue size, merge behavior, transitions
 * - Cache Settings: size, persistence, midnight reset
 * - Heatmap Settings: time window, update interval, color scheme
 * - Display Settings: auto-switch, indicators, panels, animation speed
 * - Real-time validation and feedback
 * - Reset to defaults functionality
 * - Visual sliders and toggles for intuitive configuration
 * 
 * @author BizClick WMS
 * @version 1.31 - Fase 8 Implementation
 */

import React, { useState } from 'react';
import {
  Settings,
  RotateCcw,
  Clock,
  Database,
  Flame,
  Eye,
  Zap,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Card } from '../../../common/Card';
import { useDashboardSettings, AnimationSpeed, ColorScheme, TimeWindowOption } from '../../../../contexts/DashboardSettingsContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

export const SettingsPanel: React.FC = () => {
  const { settings, updateSettings, resetSettings } = useDashboardSettings();
  const { t } = useTranslation();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  /**
   * Handle settings update with visual feedback
   */
  const handleUpdate = (updates: any) => {
    try {
      updateSettings(updates);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('[SettingsPanel] Update error:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  /**
   * Handle reset to defaults
   */
  const handleReset = () => {
    resetSettings();
    setShowResetConfirm(false);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  /**
   * Format milliseconds to seconds for display
   */
  const msToSeconds = (ms: number) => ms / 1000;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings size={32} />
            <div>
              <h1 className="text-2xl font-bold">{t('manager.modules.rackBuilderLive.settingsPanel.title', {}, 'Dashboard Innstillinger')}</h1>
              <p className="text-purple-100">{t('manager.modules.rackBuilderLive.settingsPanel.subtitle', {}, 'Konfigurer oppførsel og visning')}</p>
            </div>
          </div>

          {/* Save Status Indicator */}
          <div className="flex items-center gap-2">
            {saveStatus === 'saved' && (
              <div className="flex items-center gap-2 bg-green-500 text-white px-3 py-1.5 rounded-lg animate-fade-in">
                <CheckCircle size={16} />
                <span className="text-sm font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.saved', {}, 'Lagret')}</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-lg animate-fade-in">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">{t('common.status.error', {}, 'Feil')}</span>
              </div>
            )}

            {/* Reset Button */}
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <RotateCcw size={16} />
              <span className="text-sm font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.reset', {}, 'Tilbakestill')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-md p-6">
            <div className="text-center mb-6">
              <AlertCircle className="mx-auto mb-4 text-orange-500" size={48} />
              <h3 className="text-lg font-semibold mb-2">{t('manager.modules.rackBuilderLive.settingsPanel.resetConfirmTitle', {}, 'Tilbakestill til standard?')}</h3>
              <p className="text-sm text-gray-600">
                {t('manager.modules.rackBuilderLive.settingsPanel.resetConfirmBody', {}, 'Dette vil tilbakestille alle innstillinger til standardverdier. Denne handlingen kan ikke angres.')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                {t('manager.modules.rackBuilderLive.settingsPanel.reset', {}, 'Tilbakestill')}
              </button>
            </div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Carousel Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('manager.modules.rackBuilderLive.settingsPanel.carouselTitle', {}, 'Karusell Innstillinger')}</h2>
              <p className="text-sm text-gray-600">{t('manager.modules.rackBuilderLive.settingsPanel.carouselSubtitle', {}, 'Kontroller automatisk reol-bytte')}</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Dwell Time Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('manager.modules.rackBuilderLive.settingsPanel.dwellTimeLabel', { '0': msToSeconds(settings.carousel.dwellTime) }, `Visningstid per reol: ${msToSeconds(settings.carousel.dwellTime)} sekunder`)}
              </label>
              <input
                type="range"
                min="5000"
                max="30000"
                step="1000"
                value={settings.carousel.dwellTime}
                onChange={(e) => handleUpdate({ carousel: { dwellTime: parseInt(e.target.value) } })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5s</span>
                <span>15s</span>
                <span>30s</span>
              </div>
            </div>

            {/* Max Queue Size Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('manager.modules.rackBuilderLive.settingsPanel.maxQueueSizeLabel', { '0': settings.carousel.maxQueueSize }, `Maksimum kø-størrelse: ${settings.carousel.maxQueueSize} reoler`)}
              </label>
              <input
                type="range"
                min="10"
                max="100"
                step="10"
                value={settings.carousel.maxQueueSize}
                onChange={(e) => handleUpdate({ carousel: { maxQueueSize: parseInt(e.target.value) } })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10</span>
                <span>50</span>
                <span>100</span>
              </div>
            </div>

            {/* Merge Same Rack Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilderLive.settingsPanel.mergeSameRack', {}, 'Slå sammen samme reol')}</label>
                <p className="text-xs text-gray-500">{t('manager.modules.rackBuilderLive.settingsPanel.mergeSameRackDesc', {}, 'Kombiner hendelser fra samme reol i køen')}</p>
              </div>
              <button
                onClick={() => handleUpdate({ carousel: { mergeSameRack: !settings.carousel.mergeSameRack } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.carousel.mergeSameRack ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.carousel.mergeSameRack ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Transition Duration Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('manager.modules.rackBuilderLive.settingsPanel.transitionDurationLabel', { '0': settings.carousel.transitionDuration }, `Overgangsvarighet: ${settings.carousel.transitionDuration}ms`)}
              </label>
              <input
                type="range"
                min="200"
                max="1000"
                step="100"
                value={settings.carousel.transitionDuration}
                onChange={(e) => handleUpdate({ carousel: { transitionDuration: parseInt(e.target.value) } })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{t('manager.modules.rackBuilderLive.settingsPanel.fast200ms', {}, 'Rask (200ms)')}</span>
                <span>{t('manager.modules.rackBuilderLive.settingsPanel.slow1000ms', {}, 'Langsom (1000ms)')}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Cache Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <Database className="text-green-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('manager.modules.rackBuilderLive.settingsPanel.cacheTitle', {}, 'Cache Innstillinger')}</h2>
              <p className="text-sm text-gray-600">{t('manager.modules.rackBuilderLive.settingsPanel.cacheSubtitle', {}, 'Lagring og vedlikehold av hendelser')}</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Max Cache Size Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('manager.modules.rackBuilderLive.settingsPanel.maxCacheSizeLabel', { '0': settings.cache.maxCacheSize.toLocaleString() }, `Maksimum cache-størrelse: ${settings.cache.maxCacheSize.toLocaleString()} hendelser`)}
              </label>
              <input
                type="range"
                min="5000"
                max="20000"
                step="1000"
                value={settings.cache.maxCacheSize}
                onChange={(e) => handleUpdate({ cache: { maxCacheSize: parseInt(e.target.value) } })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5,000</span>
                <span>12,500</span>
                <span>20,000</span>
              </div>
            </div>

            {/* Persist to localStorage Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilderLive.settingsPanel.persistToBrowser', {}, 'Lagre til nettleser')}</label>
                <p className="text-xs text-gray-500">{t('manager.modules.rackBuilderLive.settingsPanel.persistToBrowserDesc', {}, 'Bevar cache ved side-refresh')}</p>
              </div>
              <button
                onClick={() => handleUpdate({ cache: { persistToLocalStorage: !settings.cache.persistToLocalStorage } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.cache.persistToLocalStorage ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.cache.persistToLocalStorage ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Reset at Midnight Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilderLive.settingsPanel.resetAtMidnight', {}, 'Nullstill ved midnatt')}</label>
                <p className="text-xs text-gray-500">{t('manager.modules.rackBuilderLive.settingsPanel.resetAtMidnightDesc', {}, 'Automatisk tøm cache kl 00:00')}</p>
              </div>
              <button
                onClick={() => handleUpdate({ cache: { resetAtMidnight: !settings.cache.resetAtMidnight } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.cache.resetAtMidnight ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.cache.resetAtMidnight ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-800">
                💡 <strong>{t('manager.modules.rackBuilderLive.settingsPanel.tipsLabel', {}, 'Tips:')}</strong> {t('manager.modules.rackBuilderLive.settingsPanel.cacheTip', {}, 'Større cache gir bedre heatmap-data, men bruker mer minne. 10,000 hendelser bruker ca. 2-3 MB.')}
              </p>
            </div>
          </div>
        </Card>

        {/* Heatmap Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Flame className="text-orange-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('manager.modules.rackBuilderLive.settingsPanel.heatmapTitle', {}, 'Heatmap Innstillinger')}</h2>
              <p className="text-sm text-gray-600">{t('manager.modules.rackBuilderLive.settingsPanel.heatmapSubtitle', {}, 'Intensitetsvisning og farger')}</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Default Time Window Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('manager.modules.rackBuilderLive.settingsPanel.defaultTimeWindow', {}, 'Standard tidsvindu')}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(['1h', '4h', '8h', 'today'] as TimeWindowOption[]).map((window) => (
                  <button
                    key={window}
                    onClick={() => handleUpdate({ heatmap: { defaultTimeWindow: window } })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      settings.heatmap.defaultTimeWindow === window
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {window === 'today' ? t('manager.modules.rackBuilderLive.timeWindow.today', {}, 'I dag') : window.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Update Interval Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('manager.modules.rackBuilderLive.settingsPanel.updateIntervalLabel', { '0': settings.heatmap.updateInterval }, `Oppdateringsintervall: ${settings.heatmap.updateInterval} sekunder`)}
              </label>
              <input
                type="range"
                min="10"
                max="60"
                step="5"
                value={settings.heatmap.updateInterval}
                onChange={(e) => handleUpdate({ heatmap: { updateInterval: parseInt(e.target.value) } })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10s</span>
                <span>35s</span>
                <span>60s</span>
              </div>
            </div>

            {/* Color Scheme Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('manager.modules.rackBuilderLive.settingsPanel.colorScheme', {}, 'Fargeskjema')}
              </label>
              <div className="space-y-2">
                {([
                  { value: 'cold-hot' as ColorScheme, label: t('manager.modules.rackBuilderLive.settingsPanel.colorColdToHot', {}, 'Kald til Varm'), colors: 'from-blue-500 via-yellow-500 to-red-500' },
                  { value: 'green-red' as ColorScheme, label: t('manager.modules.rackBuilderLive.settingsPanel.colorGreenToRed', {}, 'Grønn til Rød'), colors: 'from-green-500 via-yellow-500 to-red-500' },
                  { value: 'grayscale' as ColorScheme, label: t('manager.modules.rackBuilderLive.settingsPanel.colorGrayscaleBlind', {}, 'Gråskala (fargeblind)'), colors: 'from-gray-300 to-gray-800' }
                ]).map(({ value, label, colors }) => (
                  <button
                    key={value}
                    onClick={() => handleUpdate({ heatmap: { colorScheme: value } })}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      settings.heatmap.colorScheme === value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    <div className={`w-24 h-6 rounded bg-gradient-to-r ${colors}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Display Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Eye className="text-purple-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('manager.modules.rackBuilderLive.settingsPanel.displayTitle', {}, 'Visning Innstillinger')}</h2>
              <p className="text-sm text-gray-600">{t('manager.modules.rackBuilderLive.settingsPanel.displaySubtitle', {}, 'UI-elementer og animasjoner')}</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Auto-Switch Enabled Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilderLive.settingsPanel.autoSwitch', {}, 'Automatisk bytte')}</label>
                <p className="text-xs text-gray-500">{t('manager.modules.rackBuilderLive.settingsPanel.autoSwitchDesc', {}, 'Bytt automatisk mellom reoler')}</p>
              </div>
              <button
                onClick={() => handleUpdate({ display: { autoSwitchEnabled: !settings.display.autoSwitchEnabled } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.display.autoSwitchEnabled ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.display.autoSwitchEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Show Queue Indicator Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilderLive.settingsPanel.showQueueIndicator', {}, 'Vis kø-indikator')}</label>
                <p className="text-xs text-gray-500">{t('manager.modules.rackBuilderLive.settingsPanel.showQueueIndicatorDesc', {}, 'Badge med antall reoler i kø')}</p>
              </div>
              <button
                onClick={() => handleUpdate({ display: { showQueueIndicator: !settings.display.showQueueIndicator } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.display.showQueueIndicator ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.display.showQueueIndicator ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Show Statistics Panel Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilderLive.settingsPanel.showStatisticsPanel', {}, 'Vis statistikkpanel')}</label>
                <p className="text-xs text-gray-500">{t('manager.modules.rackBuilderLive.settingsPanel.showStatisticsPanelDesc', {}, 'Sidepanel med sanntidsstatistikk')}</p>
              </div>
              <button
                onClick={() => handleUpdate({ display: { showStatisticsPanel: !settings.display.showStatisticsPanel } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.display.showStatisticsPanel ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.display.showStatisticsPanel ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Animation Speed Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('manager.modules.rackBuilderLive.settingsPanel.animationSpeed', {}, 'Animasjonshastighet')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'slow' as AnimationSpeed, label: t('manager.modules.rackBuilderLive.settingsPanel.speedSlow', {}, 'Langsom'), icon: '🐢' },
                  { value: 'normal' as AnimationSpeed, label: t('manager.modules.rackBuilderLive.settingsPanel.speedNormal', {}, 'Normal'), icon: '⚡' },
                  { value: 'fast' as AnimationSpeed, label: t('manager.modules.rackBuilderLive.settingsPanel.speedFast', {}, 'Rask'), icon: '🚀' }
                ]).map(({ value, label, icon }) => (
                  <button
                    key={value}
                    onClick={() => handleUpdate({ display: { animationSpeed: value } })}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      settings.display.animationSpeed === value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl mb-1">{icon}</span>
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Zap className="text-purple-600 mt-0.5 flex-shrink-0" size={16} />
                <p className="text-xs text-purple-800">
                  <strong>{t('manager.modules.rackBuilderLive.settingsPanel.tipsLabel', {}, 'Tips:')}</strong> {t('manager.modules.rackBuilderLive.settingsPanel.displayTip', {}, 'Raskere animasjoner gir bedre respons, men kan være mindre synlige. Langsom er best for presentasjoner.')}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Summary Card */}
      <Card className="p-6 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{t('manager.modules.rackBuilderLive.settingsPanel.summaryTitle', {}, 'Oppsummering av innstillinger')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <p className="text-gray-500 mb-1">{t('manager.modules.rackBuilderLive.settingsPanel.summaryCarousel', {}, 'Karusell:')}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summaryDwellTime', { '0': msToSeconds(settings.carousel.dwellTime) }, `${msToSeconds(settings.carousel.dwellTime)}s visningstid`)}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summaryMaxQueue', { '0': settings.carousel.maxQueueSize }, `${settings.carousel.maxQueueSize} maks kø`)}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">{t('manager.modules.rackBuilderLive.settingsPanel.summaryCache', {}, 'Cache:')}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summaryEvents', { '0': settings.cache.maxCacheSize.toLocaleString() }, `${settings.cache.maxCacheSize.toLocaleString()} hendelser`)}</p>
            <p className="font-medium">{settings.cache.persistToLocalStorage ? t('manager.modules.rackBuilderLive.settingsPanel.summaryStored', {}, 'Lagres') : t('manager.modules.rackBuilderLive.settingsPanel.summaryNotStored', {}, 'Ikke lagret')}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">{t('manager.modules.rackBuilderLive.settingsPanel.summaryHeatmap', {}, 'Heatmap:')}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summaryWindow', { '0': settings.heatmap.defaultTimeWindow.toUpperCase() }, `${settings.heatmap.defaultTimeWindow.toUpperCase()} vindu`)}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summaryInterval', { '0': settings.heatmap.updateInterval }, `${settings.heatmap.updateInterval}s intervall`)}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">{t('manager.modules.rackBuilderLive.settingsPanel.summaryDisplay', {}, 'Visning:')}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summaryAutoSwitch', { '0': settings.display.autoSwitchEnabled ? t('manager.modules.rackBuilderLive.settingsPanel.onValue', {}, 'På') : t('manager.modules.rackBuilderLive.settingsPanel.offValue', {}, 'Av') }, `Auto-bytte: ${settings.display.autoSwitchEnabled ? 'På' : 'Av'}`)}</p>
            <p className="font-medium">{t('manager.modules.rackBuilderLive.settingsPanel.summarySpeed', { '0': settings.display.animationSpeed }, `Hastighet: ${settings.display.animationSpeed}`)}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SettingsPanel;
